import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Copy, Check, Pencil, Trash2, Download, Clock, User, Sparkles, FolderKanban } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AIOptimizeDialog from "@/components/AIOptimizeDialog";

const complexityBadge = (c) => {
  if (c === "High") return "bg-red-50 text-red-700 border-red-200";
  if (c === "Medium") return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-emerald-50 text-emerald-700 border-emerald-200";
};

export default function QueryDetailDrawer({ open, onOpenChange, query, isAdmin, onEdit, onDelete }) {
  const [copied, setCopied] = useState(false);
  const [fullQuery, setFullQuery] = useState(query);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);

  useEffect(() => {
    setSelectedVersion(null);
    if (query?.id && open) {
      // refetch to ensure versions are up-to-date
      api.get(`/queries/${query.id}`).then((r) => setFullQuery(r.data)).catch(() => setFullQuery(query));
    } else {
      setFullQuery(query);
    }
  }, [query, open]);

  if (!fullQuery) return null;

  const displayContent = selectedVersion ? selectedVersion.sql_content : fullQuery.sql_content;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(displayContent);
      setCopied(true);
      toast.success("SQL copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  const downloadSql = () => {
    const blob = new Blob([displayContent], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const safeName = fullQuery.title.replace(/[^a-z0-9]+/gi, "_");
    link.download = `${safeName}_v${selectedVersion?.version || fullQuery.current_version}.sql`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl lg:max-w-3xl p-0 flex flex-col" data-testid="query-detail-drawer">
        <SheetHeader className="px-6 py-4 border-b border-slate-200 bg-white space-y-2">
          <div className="flex items-start justify-between gap-3">
            <SheetTitle className="font-heading text-xl font-bold tracking-tight text-slate-900 leading-snug">
              {fullQuery.title}
            </SheetTitle>
            {isAdmin && (
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="ghost" size="sm"
                  className="h-8 px-2 text-slate-600 hover:text-blue-700"
                  onClick={() => onEdit(fullQuery)}
                  data-testid="drawer-edit-button"
                >
                  <Pencil className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button
                  variant="ghost" size="sm"
                  className="h-8 px-2 text-slate-600 hover:text-red-700"
                  onClick={() => onDelete(fullQuery)}
                  data-testid="drawer-delete-button"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
              {fullQuery.type}
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
              {fullQuery.product}
            </span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${complexityBadge(fullQuery.complexity)}`}>
              {fullQuery.complexity}
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono text-slate-600 bg-slate-100">
              v{fullQuery.current_version}
            </span>
            {fullQuery.project && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200">
                <FolderKanban className="w-3 h-3" /> {fullQuery.project}
              </span>
            )}
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-auto">
          <Tabs defaultValue="details" className="w-full">
            <div className="px-6 pt-4 border-b border-slate-200 bg-white sticky top-0 z-10">
              <TabsList className="bg-slate-100">
                <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
                <TabsTrigger value="sql" data-testid="tab-sql">SQL</TabsTrigger>
                <TabsTrigger value="versions" data-testid="tab-versions">
                  Versions ({fullQuery.versions?.length || 0})
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="details" className="p-6 space-y-5 m-0">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Purpose</div>
                <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {fullQuery.purpose || <span className="italic text-slate-400">No purpose provided</span>}
                </div>
              </div>
              {fullQuery.project && (
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Project</div>
                  <div className="text-sm text-slate-700 inline-flex items-center gap-1.5">
                    <FolderKanban className="w-3.5 h-3.5 text-violet-600" />
                    <span className="font-medium">{fullQuery.project}</span>
                  </div>
                </div>
              )}
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Notes</div>
                <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {fullQuery.notes || <span className="italic text-slate-400">No notes</span>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Created By</div>
                  <div className="text-sm text-slate-700 flex items-center gap-1.5"><User className="w-3.5 h-3.5" />{fullQuery.created_by}</div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Created</div>
                  <div className="text-sm font-mono text-slate-700">{new Date(fullQuery.created_at).toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Last Updated</div>
                  <div className="text-sm font-mono text-slate-700">{new Date(fullQuery.updated_at).toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Current Version</div>
                  <div className="text-sm font-mono text-slate-700">v{fullQuery.current_version}</div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="sql" className="p-6 m-0">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  {selectedVersion ? `Version ${selectedVersion.version}` : `Current (v${fullQuery.current_version})`}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline" size="sm"
                    onClick={() => setAiOpen(true)}
                    className="h-8 text-xs border-blue-300 text-blue-700 hover:bg-blue-50 hover:text-blue-800"
                    data-testid="ai-optimize-trigger"
                  >
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Optimize with AI
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={downloadSql}
                    className="h-8 text-xs"
                    data-testid="download-sql-button"
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" /> .sql
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={copyToClipboard}
                    className="h-8 text-xs"
                    data-testid="copy-sql-button"
                  >
                    {copied ? <Check className="w-3.5 h-3.5 mr-1.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                </div>
              </div>
              <pre className="sql-code-block" data-testid="sql-code-display">{displayContent}</pre>
            </TabsContent>

            <TabsContent value="versions" className="p-6 m-0">
              <div className="space-y-2" data-testid="versions-list">
                {(fullQuery.versions || []).slice().reverse().map((v) => {
                  const isCurrent = v.version === fullQuery.current_version;
                  const isSelected = selectedVersion?.version === v.version;
                  return (
                    <div
                      key={v.version}
                      onClick={() => setSelectedVersion(isCurrent ? null : v)}
                      className={`p-3 border rounded-md cursor-pointer transition-colors ${
                        isSelected ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                      }`}
                      data-testid={`version-item-${v.version}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-sm text-slate-900">v{v.version}</span>
                          {isCurrent && (
                            <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                              Current
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 font-mono flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(v.updated_at).toLocaleString()}
                        </div>
                      </div>
                      <div className="text-xs text-slate-600 mt-1.5 flex items-center gap-1">
                        <User className="w-3 h-3" /> {v.updated_by}
                      </div>
                      <div className="text-xs text-slate-500 mt-1 truncate font-mono">{v.title}</div>
                    </div>
                  );
                })}
                {(!fullQuery.versions || fullQuery.versions.length === 0) && (
                  <div className="text-sm text-slate-400 italic text-center py-8">No version history</div>
                )}
              </div>
              {selectedVersion && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-800">
                  Viewing version {selectedVersion.version} in the SQL tab. Click v{fullQuery.current_version} to view current.
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
      <AIOptimizeDialog open={aiOpen} onOpenChange={setAiOpen} query={fullQuery} />
    </Sheet>
  );
}
